// Cal, the caloly flame-blob — THE character of the app (owner decision:
// one unique character everywhere, not a generic blob).
//
// A soft, squishy blob whose crown pulls into a little flame lick: caloly's
// whole domain is energy, and the flame ties it to the Today tab's icon.
// Deliberately no body shape, gender or species (UI plan §2.1 — nothing to
// compare a body against), and rounded everywhere: it is warmth, not fire.
//
// Animation is pure CSS on grouped SVG parts (Rive stays a later drop-in
// behind the same `pose` prop): the body breathes from its base, the tip
// flickers, the eyes blink, a pseudopod waves. The global
// prefers-reduced-motion kill in index.css stops all of it.
import type { MascotPose } from "../lib/mascot";

interface Props {
  pose: MascotPose;
  size?: number | string;
  className?: string;
  /** Set false for contexts where even ambient motion would distract. */
  animated?: boolean;
  /** Cal grows little legs and walks in place — onboarding only, where the
   *  character is meant to feel like it's moving around. Rendered outside the
   *  body so overflow:visible lets them poke below the frame. */
  legs?: boolean;
}

export function Mascot({ pose, size = 72, className, animated = true, legs = false }: Props) {
  const sleeping = pose === "sleeping";
  const celebrating = pose === "celebrating";
  const thinking = pose === "thinking";
  const explaining = pose === "explaining";
  const peeking = pose === "peeking";
  const talking = pose === "talking";
  const curious = pose === "curious";
  const happy = pose === "happy";
  const sheepish = pose === "sheepish";
  const waving = pose === "greeting" || pose === "welcoming-back";

  const eyeY = thinking ? 32 : 36;
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 80 80"
      overflow="visible"
      role="img"
      aria-hidden="true"
      className={`${animated ? `mascot mascot--${pose}` : ""} ${className ?? ""}`}
      style={
        pose === "welcoming-back" ? { transform: "rotate(-4deg)" }
        : curious ? { transform: "rotate(7deg)" }
        : sheepish ? { transform: "rotate(-5deg)" }
        : undefined
      }
    >
      <defs>
        <linearGradient id="caloly-blob" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#b6f24a" />
          <stop offset="100%" stopColor="#18b6d8" />
        </linearGradient>
      </defs>

      {legs && (
        <g className="mascot-legs" aria-hidden="true">
          <path className="mascot-leg mascot-leg-l" d="M33 72 L30 92" stroke="url(#caloly-blob)" strokeWidth="7.5" strokeLinecap="round" />
          <path className="mascot-leg mascot-leg-r" d="M47 72 L50 92" stroke="url(#caloly-blob)" strokeWidth="7.5" strokeLinecap="round" />
        </g>
      )}

      <g className="mascot-body">
        {/* the flame lick — behind the crown, animated on its own */}
        <path
          className="mascot-tip"
          d="M41 15c-4-6-1-12 6-15-2 6 6 8 4 15-1.8 5.5-8.5 5.5-10 0Z"
          fill="url(#caloly-blob)"
          opacity="0.9"
        />
        {/* body — the asymmetric soft blob, sitting on its base */}
        <path
          d="M40 10C56 10 71 19 72 37c1 15-8 31-24 35C31 76 11 70 8 52 5 35 20 10 40 10Z"
          fill="url(#caloly-blob)"
          opacity="0.94"
        />
        <ellipse cx="36" cy="52" rx="18" ry="13" fill="#ffffff" opacity="0.16" />

        {/* pseudopods — which limbs exist is the pose */}
        {waving && (
          <path className="mascot-wave" d="M66 33c5-3 8-8 7-14" stroke="url(#caloly-blob)" strokeWidth="7" strokeLinecap="round" fill="none" />
        )}
        {sheepish && (
          <path d="M63 26c7-3 10-9 9-15" stroke="url(#caloly-blob)" strokeWidth="7" strokeLinecap="round" fill="none" />
        )}
        {explaining && (
          <path d="M12 44c-5-1-8-4-9-9" stroke="url(#caloly-blob)" strokeWidth="7" strokeLinecap="round" fill="none" />
        )}
        {celebrating && (
          <>
            <path d="M17 32 10 20" stroke="url(#caloly-blob)" strokeWidth="7" strokeLinecap="round" />
            <path d="M66 30l6-11" stroke="url(#caloly-blob)" strokeWidth="7" strokeLinecap="round" />
          </>
        )}

        {/* hands over the eyes — "I'm not looking" while a password is
            typed. Arms first so the paws sit on top of them. */}
        {peeking && (
          <>
            <path d="M17 50 30 38" stroke="url(#caloly-blob)" strokeWidth="7" strokeLinecap="round" />
            <path d="M63 50 52 38" stroke="url(#caloly-blob)" strokeWidth="7" strokeLinecap="round" />
            <circle cx="32" cy="36" r="7" fill="url(#caloly-blob)" stroke="#0a0a0a" strokeOpacity="0.14" strokeWidth="1.5" />
            <circle cx="50" cy="36" r="7" fill="url(#caloly-blob)" stroke="#0a0a0a" strokeOpacity="0.14" strokeWidth="1.5" />
          </>
        )}
        {/* eyes */}
        {sleeping && (
          <>
            <path d="M28 38q4 3 8 0" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            <path d="M46 38q4 3 8 0" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          </>
        )}
        {!sleeping && !peeking && (
          <g className="mascot-eyes">
            <circle cx="32" cy={eyeY} r="3.6" fill="#0a0a0a" />
            <circle cx="50" cy={eyeY} r="3.6" fill="#0a0a0a" />
            <circle cx="33.2" cy={eyeY - 1.2} r="1.1" fill="#ffffff" />
            <circle cx="51.2" cy={eyeY - 1.2} r="1.1" fill="#ffffff" />
          </g>
        )}
        {/* mouth — every pose has its own */}
        {!sleeping && (
          talking ? <ellipse className="mascot-mouth" cx="41" cy="50" rx="5" ry="3.6" fill="#0a0a0a" />
          : happy ? <path d="M32 47q9 9 18 0" stroke="#0a0a0a" strokeWidth="3" strokeLinecap="round" fill="none" />
          : curious ? <ellipse cx="41" cy="50" rx="2.6" ry="2.6" fill="#0a0a0a" />
          : sheepish ? <path d="M35 51q6 -4 12 0" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          : celebrating ? <ellipse cx="41" cy="50" rx="5.5" ry="4.5" fill="#0a0a0a" />
          : explaining ? <ellipse cx="41" cy="50" rx="3" ry="2.5" fill="#0a0a0a" />
          : thinking ? <path d="M37 50h8" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" />
          : peeking ? <path d="M36 49q5 4 10 0" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          : pose === "greeting" ? <path d="M33 48q8 7 16 0" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
          : <path d="M35 48q6 5 12 0" stroke="#0a0a0a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
        )}
      </g>

      {/* floaters live outside the breathing body */}
      {sleeping && (
        <>
          <text className="mascot-z" x="58" y="24" fontSize="10" fill="#ffffff">z</text>
          <text className="mascot-z mascot-z2" x="65" y="16" fontSize="7" fill="#ffffff">z</text>
        </>
      )}
      {thinking && (
        <>
          <circle className="mascot-dot" cx="62" cy="18" r="2" fill="#ffffff" />
          <circle className="mascot-dot mascot-dot2" cx="68" cy="11" r="2.6" fill="#ffffff" />
        </>
      )}
      {celebrating && (
        <>
          <circle className="mascot-confetti" cx="12" cy="12" r="2" fill="#fa2d6e" />
          <circle className="mascot-confetti mascot-confetti2" cx="70" cy="20" r="2" fill="#b6f24a" />
          <circle className="mascot-confetti mascot-confetti3" cx="18" cy="26" r="1.6" fill="#18b6d8" />
          <circle className="mascot-confetti mascot-confetti2" cx="64" cy="8" r="1.6" fill="#7c5cff" />
        </>
      )}
    </svg>
  );
}
